import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { Card } from '@/components/ui/card';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SectionDivider, StatRow, StatTile } from '@/components/ui/section-card';
import { AppointmentStatusPill } from '@/components/schedule/appointment-status-pill';
import { AvailabilityPicker } from '@/components/schedule/availability-picker';
import { ProviderAppointmentLocation } from '@/components/schedule/provider-appointment-location';
import {
  useProviderAgenda,
  useProviderAppointmentAction,
  useProviderAvailability,
  useScheduleSettings,
} from '@/hooks/use-appointments';
import { getApiErrorMessage } from '@/utils/api-error';
import { showPlanLimit } from '@/store/planLimitStore';
import { useToastStore } from '@/store/toastStore';
import { APPOINTMENT_STATUS, AvailabilitySlot } from '@/types/appointment';
import { formatAppointmentWhen, formatSlotRange } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';

function ActionButton({
  icon,
  label,
  onPress,
  tone = 'neutral',
  disabled,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
  tone?: 'primary' | 'neutral' | 'danger';
  disabled?: boolean;
}) {
  const th = useThemedHex();
  const styles = {
    primary: { bg: '#FD5001', border: '#FD5001', text: '#fff', icon: '#fff' },
    neutral: { bg: th('#fff'), border: th('#E5E5E5'), text: th('#404040'), icon: th('#525252') },
    danger: { bg: th('#FEF2F2'), border: th('#FECACA'), text: th('#B91C1C'), icon: th('#B91C1C') },
  }[tone];
  return (
    <PressableScale
      haptic
      disabled={disabled}
      onPress={onPress}
      className="flex-row items-center justify-center gap-2 rounded-full py-3.5"
      style={{
        backgroundColor: styles.bg,
        borderWidth: 1,
        borderColor: styles.border,
        borderCurve: 'continuous',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Ionicons name={icon} size={18} color={styles.icon} />
      <ThemedText className="font-bold text-sm" style={{ color: styles.text }}>
        {label}
      </ThemedText>
    </PressableScale>
  );
}

export default function AppointmentDetailScreen() {
  const th = useThemedHex();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { addToast } = useToastStore();
  const settings = useScheduleSettings();
  const action = useProviderAppointmentAction();

  const range = useMemo(() => {
    const from = new Date(Date.now() - 30 * 86400000).toISOString();
    const to = new Date(
      Date.now() + (settings.data?.bookingHorizonDays ?? 90) * 86400000,
    ).toISOString();
    return { from, to };
  }, [settings.data?.bookingHorizonDays]);

  const agenda = useProviderAgenda(range.from, range.to);
  const appt = agenda.data?.find((a) => a.id === id);

  const [reasonOpen, setReasonOpen] = useState<null | 'reject' | 'cancel'>(null);
  const [reason, setReason] = useState('');
  const [reschedOpen, setReschedOpen] = useState(false);
  const [newSlot, setNewSlot] = useState<AvailabilitySlot | null>(null);

  // Mesma agenda real da criação de contrato: grade + bloqueios + ocupação.
  const reschedRange = useMemo(() => {
    const now = new Date();
    const to = new Date(now.getTime() + (settings.data?.bookingHorizonDays ?? 90) * 86400000);
    return { from: now.toISOString(), to: to.toISOString() };
  }, [settings.data?.bookingHorizonDays]);
  const reschedAvailability = useProviderAvailability({
    serviceId: appt?.serviceId,
    from: reschedRange.from,
    to: reschedRange.to,
    enabled: reschedOpen,
  });
  const reschedDays = reschedAvailability.data?.days ?? [];
  const reschedHasSlots = reschedDays.some((d) => d.available && d.slots.length > 0);

  const anyPending =
    action.approve.isPending ||
    action.reject.isPending ||
    action.cancel.isPending ||
    action.reschedule.isPending ||
    action.noShow.isPending;

  const ok = (msg: string) => () => {
    addToast(msg, 'success');
    router.back();
  };
  const fail = (e: any) => {
    if (showPlanLimit(e)) return;
    addToast(getApiErrorMessage(e, 'Não foi possível concluir.'), 'error');
  };

  if (agenda.isLoading || settings.isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas">
        <ActivityIndicator size="large" color="#FD5001" />
      </View>
    );
  }

  if (!appt) {
    return (
      <PageLayout title="Agendamento" onBack={() => router.back()}>
        <View className="items-center justify-center gap-2 px-8 py-16">
          <Ionicons className="text-neutral-400" name="help-circle-outline" size={40} />
          <ThemedText className="text-center text-neutral-500">
            Agendamento não encontrado.
          </ThemedText>
        </View>
      </PageLayout>
    );
  }

  const pending = appt.status === APPOINTMENT_STATUS.PENDING;
  const confirmed = appt.status === APPOINTMENT_STATUS.CONFIRMED;
  const started = new Date(appt.startAt).getTime() < Date.now();

  const submitReason = () => {
    const trimmed = reason.trim() || undefined;
    const mode = reasonOpen;
    setReasonOpen(null);
    setReason('');
    if (mode === 'reject') {
      action.reject.mutate(
        { id: appt.id, reason: trimmed },
        { onSuccess: ok('Pedido recusado.'), onError: fail },
      );
    } else if (mode === 'cancel') {
      action.cancel.mutate(
        { id: appt.id, reason: trimmed },
        { onSuccess: ok('Agendamento cancelado.'), onError: fail },
      );
    }
  };

  const submitReschedule = () => {
    if (!newSlot) return;
    action.reschedule.mutate(
      { id: appt.id, startAt: newSlot.startAt },
      {
        onSuccess: ok('Agendamento remarcado.'),
        onError: (e) => {
          fail(e);
          setNewSlot(null);
          void reschedAvailability.refetch();
        },
      },
    );
  };

  return (
    <>
      <PageLayout title="Agendamento" onBack={() => router.back()}>
        {/* Resumo: quem, quando e onde — nessa ordem */}
        <Card className="p-5">
          <View className="flex-row items-start justify-between gap-3">
            <View className="min-w-0 flex-1">
              <ThemedText className="font-bold text-xl text-neutral-900">
                {appt.clientName ?? 'Cliente'}
              </ThemedText>
              <ThemedText className="text-xs text-neutral-500">
                {appt.serviceName ?? 'Serviço'}
              </ThemedText>
            </View>
            <AppointmentStatusPill status={appt.status} />
          </View>

          <View className="mt-4 gap-4">
            <StatRow>
              <StatTile label="Quando" value={formatAppointmentWhen(appt.startAt)} />
              <StatTile label="Horário" value={formatSlotRange(appt.startAt, appt.endAt)} />
            </StatRow>

            {appt.isOverbook && (
              <View className="flex-row items-center gap-2 rounded-2xl bg-amber-50 px-3 py-2">
                <Ionicons className="text-amber-700" name="flash-outline" size={15} />
                <ThemedText className="font-semibold text-xs text-amber-700">
                  Encaixe fora da grade
                </ThemedText>
              </View>
            )}

            <SectionDivider />

            <ProviderAppointmentLocation appointment={appt} />

            {!!appt.cancelReason && (
              <View className="rounded-2xl bg-neutral-50 p-3">
                <ThemedText className="text-xs text-neutral-500">Motivo informado</ThemedText>
                <ThemedText className="text-sm leading-5 text-neutral-700">
                  {appt.cancelReason}
                </ThemedText>
              </View>
            )}
          </View>
        </Card>

        {/* Ações */}
        {(pending || confirmed) && (
          <View className="gap-2.5">
            {pending && (
              <>
                <ActionButton
                  icon="checkmark-circle"
                  label="Aprovar"
                  tone="primary"
                  disabled={anyPending}
                  onPress={() =>
                    action.approve.mutate(appt.id, { onSuccess: ok('Aprovado.'), onError: fail })
                  }
                />
                <ActionButton
                  icon="close-circle-outline"
                  label="Recusar"
                  tone="neutral"
                  disabled={anyPending}
                  onPress={() => setReasonOpen('reject')}
                />
              </>
            )}
            {confirmed && (
              <>
                <ActionButton
                  icon="swap-horizontal-outline"
                  label="Remarcar"
                  tone="neutral"
                  disabled={anyPending}
                  onPress={() => setReschedOpen((v) => !v)}
                />
                {started && (
                  <ActionButton
                    icon="alert-circle-outline"
                    label="Marcar falta"
                    tone="neutral"
                    disabled={anyPending}
                    onPress={() =>
                      action.noShow.mutate(appt.id, {
                        onSuccess: ok('Falta registrada.'),
                        onError: fail,
                      })
                    }
                  />
                )}
                <ActionButton
                  icon="trash-outline"
                  label="Cancelar"
                  tone="danger"
                  disabled={anyPending}
                  onPress={() => setReasonOpen('cancel')}
                />
              </>
            )}
          </View>
        )}

        {/* Painel remarcar */}
        {reschedOpen && confirmed && (
          <View className="gap-3 rounded-2xl bg-card p-4" style={{ borderCurve: 'continuous' }}>
            <ThemedText className="font-bold text-sm text-neutral-900">Novo horário</ThemedText>
            {reschedAvailability.isLoading ? (
              <View className="items-center py-10">
                <ActivityIndicator size="small" color="#FD5001" />
              </View>
            ) : reschedHasSlots ? (
              <>
                <AvailabilityPicker
                  days={reschedDays}
                  selectedStart={newSlot?.startAt ?? null}
                  onSelect={setNewSlot}
                />
                <PressableScale
                  haptic
                  disabled={!newSlot || action.reschedule.isPending}
                  onPress={submitReschedule}
                  className="flex-row items-center justify-center gap-2 rounded-full py-3"
                  style={{ backgroundColor: newSlot ? '#FD5001' : th('#D4D4D4') }}
                >
                  {action.reschedule.isPending ? (
                    <ActivityIndicator color="#fff" size="small" />
                  ) : (
                    <ThemedText className="font-bold text-sm text-white">
                      {newSlot
                        ? `Remarcar para ${formatAppointmentWhen(newSlot.startAt)}`
                        : 'Escolha um horário'}
                    </ThemedText>
                  )}
                </PressableScale>
              </>
            ) : (
              <ThemedText className="text-xs text-neutral-500">
                Sem horários livres na agenda nos próximos {settings.data?.bookingHorizonDays ?? 90}{' '}
                dias.
              </ThemedText>
            )}
          </View>
        )}
      </PageLayout>

      {/* Modal motivo */}
      <Modal
        visible={reasonOpen !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setReasonOpen(null)}
      >
        <KeyboardAvoidingView behavior="padding" style={{ flex: 1 }}>
          <Pressable className="flex-1 justify-end bg-black/40" onPress={() => setReasonOpen(null)}>
            <Pressable
              onPress={(e) => e.stopPropagation()}
              className="gap-3 rounded-t-3xl bg-card px-5 pb-8 pt-5"
              style={{ borderCurve: 'continuous' }}
            >
              <ThemedText className="font-bold text-base text-neutral-900">
                {reasonOpen === 'reject' ? 'Recusar pedido' : 'Cancelar agendamento'}
              </ThemedText>
              <ThemedText className="text-xs text-neutral-500">
                O motivo é opcional e chega ao cliente.
              </ThemedText>
              <TextInput
                value={reason}
                onChangeText={setReason}
                placeholder="Motivo (opcional)"
                placeholderTextColor="#a3a3a3"
                multiline
                className="min-h-20 rounded-xl border border-neutral-200 bg-card px-3.5 py-2.5 text-sm text-neutral-900"
                style={{ borderCurve: 'continuous', textAlignVertical: 'top' }}
              />
              <PressableScale
                haptic
                onPress={submitReason}
                className="flex-row items-center justify-center gap-2 rounded-full py-3.5"
                style={{ backgroundColor: reasonOpen === 'cancel' ? th('#B91C1C') : '#FD5001' }}
              >
                <ThemedText className="font-bold text-sm text-white">
                  {reasonOpen === 'reject' ? 'Recusar' : 'Cancelar agendamento'}
                </ThemedText>
              </PressableScale>
            </Pressable>
          </Pressable>
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}
